import { useEffect, useRef, useState, type DragEvent } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCompetitor,
  updateCompetitor,
  deleteCompetitor,
  uploadCompetitorLogo,
  deleteCompetitorLogo,
  uploadCompetitorFile,
  deleteCompetitorFile,
  moveCompetitorFile,
  competitorFileUrl,
  competitorFilePreviewUrl,
  createCompetitorFolder,
  renameCompetitorFolder,
  moveCompetitorFolder,
  deleteCompetitorFolder,
  type CompetitorFile,
  type CompetitorFolder,
} from "@/lib/api";
import { EditableLogo } from "@/components/EntityLogo";
import { DocPreviewModal, canPreviewDoc } from "@/components/DocPreviewModal";

const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs font-medium text-text-muted";

const isImage = (mime: string) => mime.startsWith("image/");

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function CompetitorModal({
  competitorId,
  onClose,
}: {
  competitorId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: competitor } = useQuery({
    queryKey: ["competitor", competitorId],
    queryFn: () => getCompetitor(competitorId),
  });

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (competitor) {
      setName(competitor.name);
      setDescription(competitor.description ?? "");
      setWebsite(competitor.website ?? "");
      setDirty(false);
    }
  }, [competitor]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["competitor", competitorId] });
    qc.invalidateQueries({ queryKey: ["competitors"] });
  };

  const save = useMutation({
    mutationFn: () =>
      updateCompetitor(competitorId, {
        name,
        description: description || null,
        website: website || null,
      }),
    onSuccess: () => {
      invalidate();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteCompetitor(competitorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["competitors"] });
      onClose();
    },
  });

  const logoUpload = useMutation({
    mutationFn: (file: File) => uploadCompetitorLogo(competitorId, file),
    onSuccess: invalidate,
  });
  const logoRemove = useMutation({
    mutationFn: () => deleteCompetitorLogo(competitorId),
    onSuccess: invalidate,
  });

  const mark =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setDirty(true);
    };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-[2px] sm:p-8"
      onClick={onClose}
    >
      <div
        className="chat-fade flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg-elevated shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-6 py-3.5">
          <span className="text-sm text-text-muted">Competitor</span>
          <span className="flex-1" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (
                  confirm(
                    "Delete this competitor? Its pictures and files go too. This cannot be undone.",
                  )
                )
                  remove.mutate();
              }}
              className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger/10"
            >
              <i className="fa-solid fa-trash mr-1" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        {!competitor ? (
          <div className="grid min-h-[16rem] flex-1 place-items-center text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-xl" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid gap-8 p-6 md:grid-cols-[minmax(0,1fr)_240px]">
              {/* left: identity + description + files */}
              <div className="min-w-0">
                <div className="mb-5 flex items-center gap-3">
                  <EditableLogo
                    name={competitor.name}
                    url={competitor.logoUrl}
                    size={52}
                    fallbackIcon="fa-chess-knight"
                    uploading={logoUpload.isPending}
                    onUpload={(f) => logoUpload.mutate(f)}
                    onRemove={() => logoRemove.mutate()}
                  />
                  <input
                    className="w-full min-w-0 flex-1 rounded-token-md border border-transparent bg-transparent px-2 py-1 font-display text-2xl font-semibold leading-snug text-text-heading outline-none transition-colors hover:border-border focus:border-accent"
                    value={name}
                    onChange={(e) => mark(setName)(e.target.value)}
                  />
                </div>

                <label className={labelCls}>
                  <i className="fa-solid fa-align-left mr-1.5 text-[0.6rem]" />
                  What they do
                </label>
                <textarea
                  className={`${inputCls} mb-6`}
                  rows={5}
                  placeholder="Their product, positioning, pricing, strengths and weaknesses…"
                  value={description}
                  onChange={(e) => mark(setDescription)(e.target.value)}
                />

                <FilesSection
                  competitorId={competitorId}
                  folders={competitor.folders}
                  files={competitor.files}
                  onChanged={invalidate}
                />
              </div>

              {/* right: details panel */}
              <aside className="h-fit rounded-token-md border border-border bg-bg-card p-4 md:sticky md:top-0">
                <p className="mb-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
                  Details
                </p>
                <label className={labelCls}>Website</label>
                <input
                  className={inputCls}
                  value={website}
                  placeholder="https://…"
                  onChange={(e) => mark(setWebsite)(e.target.value)}
                />
                {competitor.website && (
                  <a
                    href={competitor.website}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 text-xs text-accent hover:underline"
                  >
                    <i className="fa-solid fa-arrow-up-right-from-square" />
                    Visit site
                  </a>
                )}
                <p className="mt-3 border-t border-border-light pt-3 text-[11px] text-text-muted">
                  Added {new Date(competitor.createdAt).toLocaleDateString()}
                </p>
              </aside>
            </div>
          </div>
        )}

        {/* footer save bar */}
        {competitor && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-bg-card px-6 py-3">
            {saved && (
              <span className="text-xs text-accent">
                <i className="fa-solid fa-check mr-1" />
                Saved
              </span>
            )}
            {dirty && !saved && <span className="text-xs text-text-muted">Unsaved changes</span>}
            <button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

// ── Folder tree helpers ───────────────────────────────────────────────────

type FolderNode = CompetitorFolder & { children: FolderNode[] };

/** Build a sorted tree from the flat folder list. */
function buildTree(folders: CompetitorFolder[]): FolderNode[] {
  const byId = new Map<string, FolderNode>();
  for (const f of folders) byId.set(f.id, { ...f, children: [] });
  const roots: FolderNode[] = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortRec = (nodes: FolderNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

/** Every folder nested beneath rootId (exclusive of rootId itself). */
function collectDescendants(folders: CompetitorFolder[], rootId: string): Set<string> {
  const ids = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop() as string;
    for (const f of folders) {
      if (f.parentId === cur && !ids.has(f.id)) {
        ids.add(f.id);
        stack.push(f.id);
      }
    }
  }
  return ids;
}

/** A file or folder currently being dragged. */
type Drag = { type: "file" | "folder"; id: string };

/** Shared handlers threaded through the recursive folder tree. */
interface TreeCtx {
  filesOf: (folderId: string | null) => CompetitorFile[];
  uploading: boolean;
  dropTargetProps: (folderId: string | null) => {
    active: boolean;
    onDragOver: (e: DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: DragEvent) => void;
  };
  startDragFile: (id: string, e: DragEvent) => void;
  startDragFolder: (id: string, e: DragEvent) => void;
  endDrag: () => void;
  onPreview: (file: CompetitorFile) => void;
  onUploadInto: (folderId: string) => void;
  onNewSubfolder: (name: string, parentId: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string, name: string) => void;
  onDeleteFile: (id: string, name: string) => void;
}

/**
 * Files organized into a folder tree. Pictures render as a gallery, other
 * documents as a list. Supports nested folders, multi-file upload, folder
 * upload (which recreates the directory structure), and drag-and-drop to
 * move files or whole folders between folders (or out to the root).
 */
function FilesSection({
  competitorId,
  folders,
  files,
  onChanged,
}: {
  competitorId: string;
  folders: CompetitorFolder[];
  files: CompetitorFile[];
  onChanged: () => void;
}) {
  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  // Where the next "Upload files" pick lands — set just before opening the picker.
  const pendingFolderId = useRef<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [dragging, setDragging] = useState<Drag | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const [preview, setPreview] = useState<CompetitorFile | null>(null);

  // `webkitdirectory` isn't a standard React attribute — set it on the node.
  useEffect(() => {
    const el = folderInputRef.current;
    if (el) {
      el.setAttribute("webkitdirectory", "");
      el.setAttribute("directory", "");
    }
  }, []);

  const uploadFiles = useMutation({
    mutationFn: async ({
      files: picked,
      folderId,
    }: {
      files: File[];
      folderId: string | null;
    }) => {
      for (const f of picked) await uploadCompetitorFile(competitorId, f, folderId);
    },
    onSuccess: onChanged,
  });

  const uploadFolder = useMutation({
    mutationFn: async (picked: File[]) => {
      // Recreate the directory tree: each path prefix becomes a nested folder.
      const created = new Map<string, string>(); // path key -> folderId
      const ensurePath = async (segments: string[]): Promise<string | null> => {
        let parentId: string | null = null;
        let key = "";
        for (const seg of segments) {
          key = key ? `${key}/${seg}` : seg;
          let id = created.get(key);
          if (!id) {
            const folder = await createCompetitorFolder(competitorId, seg, parentId);
            id = folder.id;
            created.set(key, id);
          }
          parentId = id;
        }
        return parentId;
      };
      for (const f of picked) {
        const rel =
          (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name;
        const parts = rel.split("/");
        const dirs = parts.slice(0, -1); // drop the filename
        const folderId = await ensurePath(dirs);
        await uploadCompetitorFile(competitorId, f, folderId);
      }
    },
    onSuccess: onChanged,
  });

  const createFolder = useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId?: string | null }) =>
      createCompetitorFolder(competitorId, name, parentId),
    onSuccess: () => {
      setFolderName("");
      setCreatingFolder(false);
      onChanged();
    },
  });

  const removeFolder = useMutation({
    mutationFn: (id: string) => deleteCompetitorFolder(id),
    onSuccess: onChanged,
  });
  const renameFolder = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameCompetitorFolder(id, name),
    onSuccess: onChanged,
  });
  const moveFolder = useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) =>
      moveCompetitorFolder(id, parentId),
    onSuccess: onChanged,
  });
  const moveFile = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      moveCompetitorFile(id, folderId),
    onSuccess: onChanged,
  });
  const removeFile = useMutation({
    mutationFn: (id: string) => deleteCompetitorFile(id),
    onSuccess: onChanged,
  });

  const busy = uploadFiles.isPending || uploadFolder.isPending;
  const uploadError = (uploadFiles.error ?? uploadFolder.error) as Error | null;

  function pickFiles(folderId: string | null) {
    pendingFolderId.current = folderId;
    filesInputRef.current?.click();
  }

  const canDropOn = (folderId: string | null): boolean => {
    if (!dragging) return false;
    if (dragging.type === "file") return true;
    // A folder can't drop onto itself or into one of its own descendants.
    if (folderId === dragging.id) return false;
    if (folderId && collectDescendants(folders, dragging.id).has(folderId)) return false;
    return true;
  };

  const handleDrop = (folderId: string | null) => {
    if (!dragging) return;
    if (dragging.type === "file") moveFile.mutate({ id: dragging.id, folderId });
    else moveFolder.mutate({ id: dragging.id, parentId: folderId });
    setDragging(null);
    setDragOverKey(null);
  };

  const ctx: TreeCtx = {
    filesOf: (folderId) => files.filter((f) => f.folderId === folderId),
    uploading: busy,
    dropTargetProps: (folderId) => {
      const key = folderId ?? "root";
      return {
        active: dragOverKey === key && canDropOn(folderId),
        onDragOver: (e) => {
          if (!canDropOn(folderId)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "move";
          setDragOverKey(key);
        },
        onDragLeave: () => setDragOverKey((k) => (k === key ? null : k)),
        onDrop: (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDrop(folderId);
        },
      };
    },
    startDragFile: (id, e) => {
      e.dataTransfer.setData("text/plain", `file:${id}`);
      e.dataTransfer.effectAllowed = "move";
      setDragging({ type: "file", id });
    },
    startDragFolder: (id, e) => {
      e.dataTransfer.setData("text/plain", `folder:${id}`);
      e.dataTransfer.effectAllowed = "move";
      setDragging({ type: "folder", id });
    },
    endDrag: () => {
      setDragging(null);
      setDragOverKey(null);
    },
    onPreview: (file) => {
      if (canPreviewDoc(file.filename, file.mime)) setPreview(file);
      else window.open(competitorFileUrl(file.id), "_blank", "noopener");
    },
    onUploadInto: (folderId) => pickFiles(folderId),
    onNewSubfolder: (name, parentId) => createFolder.mutate({ name, parentId }),
    onRenameFolder: (id, name) => renameFolder.mutate({ id, name }),
    onDeleteFolder: (id, name) => {
      if (confirm(`Delete folder "${name}" and everything inside it?`))
        removeFolder.mutate(id);
    },
    onDeleteFile: (id, name) => {
      if (confirm(`Delete "${name}"?`)) removeFile.mutate(id);
    },
  };

  const tree = buildTree(folders);
  const rootFiles = ctx.filesOf(null);
  const rootDrop = ctx.dropTargetProps(null);

  return (
    <div className="mb-4">
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <label className={labelCls}>
          <i className="fa-solid fa-paperclip mr-1.5 text-[0.6rem]" />
          Pictures &amp; files
          {files.length > 0 && (
            <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 text-[10px] text-text-muted">
              {files.length}
            </span>
          )}
        </label>
        <span className="flex-1" />
        <button
          onClick={() => setCreatingFolder((v) => !v)}
          className="text-xs font-medium text-accent hover:underline"
        >
          {creatingFolder ? "Cancel" : "+ New folder"}
        </button>
        <button
          onClick={() => pickFiles(null)}
          disabled={busy}
          className="text-xs font-medium text-accent hover:underline disabled:opacity-50"
        >
          {busy ? "Uploading…" : "+ Upload files"}
        </button>
        <button
          onClick={() => folderInputRef.current?.click()}
          disabled={busy}
          className="text-xs font-medium text-accent hover:underline disabled:opacity-50"
        >
          + Upload folder
        </button>
      </div>

      {creatingFolder && (
        <form
          className="mb-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (folderName.trim()) createFolder.mutate({ name: folderName.trim() });
          }}
        >
          <input
            className={inputCls}
            placeholder="Folder name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            autoFocus
            required
          />
          <button
            type="submit"
            disabled={createFolder.isPending}
            className="shrink-0 rounded-token-md bg-accent text-on-accent px-3 text-sm font-semibold disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            <i className="fa-solid fa-check" />
          </button>
        </form>
      )}

      {uploadError && (
        <p className="mb-2 text-xs text-danger">{uploadError.message || "Upload failed"}</p>
      )}

      {/* Shared, hidden inputs driven by the buttons above and folder headers. */}
      <input
        ref={filesInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (picked.length)
            uploadFiles.mutate({ files: picked, folderId: pendingFolderId.current });
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (picked.length) uploadFolder.mutate(picked);
        }}
      />

      {folders.length === 0 && files.length === 0 ? (
        <p className="text-xs text-text-muted">
          No pictures or files yet — upload screenshots, brochures, or docs, or make a folder.
        </p>
      ) : (
        <div
          className={`space-y-3 rounded-token-md p-1 transition-colors ${
            rootDrop.active ? "bg-accent-muted/60 ring-1 ring-accent" : ""
          }`}
          onDragOver={rootDrop.onDragOver}
          onDragLeave={rootDrop.onDragLeave}
          onDrop={rootDrop.onDrop}
        >
          {tree.map((node) => (
            <FolderBlock key={node.id} node={node} ctx={ctx} depth={0} />
          ))}

          {rootFiles.length > 0 && <FileGrid files={rootFiles} ctx={ctx} />}

          {dragging && (
            <p className="px-1 text-[11px] text-text-muted">
              Drop on a folder to move it there, or here to move it to the top level.
            </p>
          )}
        </div>
      )}

      {preview && (
        <DocPreviewModal
          name={preview.filename}
          url={competitorFileUrl(preview.id)}
          previewUrl={competitorFilePreviewUrl(preview.id)}
          mime={preview.mime}
          size={preview.size}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

/** One folder in the tree: a collapsible, draggable header over its children. */
function FolderBlock({ node, ctx, depth }: { node: FolderNode; ctx: TreeCtx; depth: number }) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(node.name);
  const [creatingSub, setCreatingSub] = useState(false);
  const [subName, setSubName] = useState("");

  const folderFiles = ctx.filesOf(node.id);
  const drop = ctx.dropTargetProps(node.id);

  const commit = () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== node.name) ctx.onRenameFolder(node.id, trimmed);
    else setName(node.name);
    setEditing(false);
  };

  return (
    <div
      className={`rounded-token-md border bg-bg-card transition-colors ${
        drop.active ? "border-accent ring-1 ring-accent" : "border-border"
      }`}
      onDragOver={drop.onDragOver}
      onDragLeave={drop.onDragLeave}
      onDrop={drop.onDrop}
    >
      <div
        className="flex items-center gap-2 px-3 py-2"
        draggable={!editing}
        onDragStart={(e) => ctx.startDragFolder(node.id, e)}
        onDragEnd={ctx.endDrag}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          title={open ? "Collapse" : "Expand"}
          className="grid h-5 w-5 place-items-center text-text-muted hover:text-text-heading"
        >
          <i
            className={`fa-solid fa-chevron-right text-[10px] transition-transform ${
              open ? "rotate-90" : ""
            }`}
          />
        </button>
        <i
          className={`fa-solid ${open ? "fa-folder-open" : "fa-folder"} text-accent`}
          title="Drag to move this folder"
        />
        {editing ? (
          <input
            className="min-w-0 flex-1 rounded border border-border bg-bg px-2 py-0.5 text-sm text-text outline-none focus:border-accent"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") {
                setName(node.name);
                setEditing(false);
              }
            }}
            autoFocus
          />
        ) : (
          <button
            onClick={() => setEditing(true)}
            title="Rename folder"
            className="min-w-0 flex-1 truncate text-left text-sm font-medium text-text-heading"
          >
            {node.name}
          </button>
        )}
        <span className="shrink-0 text-xs text-text-muted">{folderFiles.length}</span>
        <button
          onClick={() => {
            setCreatingSub(true);
            setOpen(true);
          }}
          title="New subfolder"
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-xs text-text-muted hover:text-accent"
        >
          <i className="fa-solid fa-folder-plus" />
        </button>
        <button
          onClick={() => ctx.onUploadInto(node.id)}
          disabled={ctx.uploading}
          title="Upload into folder"
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-xs text-text-muted hover:text-accent disabled:opacity-50"
        >
          <i className="fa-solid fa-arrow-up-from-bracket" />
        </button>
        <button
          onClick={() => ctx.onDeleteFolder(node.id, node.name)}
          title="Delete folder"
          className="grid h-6 w-6 shrink-0 place-items-center rounded text-xs text-text-muted hover:text-danger"
        >
          <i className="fa-solid fa-trash" />
        </button>
      </div>

      {open && (
        <div className="space-y-2 border-t border-border-light p-3">
          {creatingSub && (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (subName.trim()) {
                  ctx.onNewSubfolder(subName.trim(), node.id);
                  setSubName("");
                  setCreatingSub(false);
                }
              }}
            >
              <input
                className={inputCls}
                placeholder="Subfolder name"
                value={subName}
                onChange={(e) => setSubName(e.target.value)}
                autoFocus
                required
              />
              <button
                type="submit"
                className="shrink-0 rounded-token-md bg-accent text-on-accent px-3 text-sm font-semibold"
                style={{ color: "var(--color-on-accent)" }}
              >
                <i className="fa-solid fa-check" />
              </button>
              <button
                type="button"
                onClick={() => setCreatingSub(false)}
                className="shrink-0 rounded-token-md border border-border px-3 text-xs text-text-secondary"
              >
                Cancel
              </button>
            </form>
          )}

          {node.children.map((child) => (
            <FolderBlock key={child.id} node={child} ctx={ctx} depth={depth + 1} />
          ))}

          {folderFiles.length > 0 ? (
            <FileGrid files={folderFiles} ctx={ctx} />
          ) : (
            node.children.length === 0 &&
            !creatingSub && (
              <p className="text-xs text-text-muted">
                Empty — drop files here, or use the upload button above.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}

/** Renders a set of files: pictures as a gallery, other documents as a list. */
function FileGrid({ files, ctx }: { files: CompetitorFile[]; ctx: TreeCtx }) {
  const images = files.filter((f) => isImage(f.mime));
  const docs = files.filter((f) => !isImage(f.mime));

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {images.map((f) => (
            <div
              key={f.id}
              draggable
              onDragStart={(e) => ctx.startDragFile(f.id, e)}
              onDragEnd={ctx.endDrag}
              className="group relative aspect-square cursor-grab overflow-hidden rounded-token-md border border-border bg-bg active:cursor-grabbing"
            >
              <button
                type="button"
                onClick={() => ctx.onPreview(f)}
                title={`Preview ${f.filename}`}
                className="block h-full w-full"
              >
                <img
                  src={competitorFileUrl(f.id)}
                  alt={f.filename}
                  draggable={false}
                  className="h-full w-full object-cover"
                />
              </button>
              <button
                onClick={() => ctx.onDeleteFile(f.id, f.filename)}
                title="Delete picture"
                className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded bg-black/55 text-[11px] text-white opacity-0 transition-opacity hover:bg-danger group-hover:opacity-100"
              >
                <i className="fa-solid fa-trash" />
              </button>
            </div>
          ))}
        </div>
      )}

      {docs.length > 0 && (
        <ul className="divide-y divide-border-light rounded-token-md border border-border bg-bg">
          {docs.map((f) => (
            <li
              key={f.id}
              draggable
              onDragStart={(e) => ctx.startDragFile(f.id, e)}
              onDragEnd={ctx.endDrag}
              className="group flex cursor-grab items-center gap-3 px-3 py-2 active:cursor-grabbing"
            >
              <i className="fa-solid fa-file w-4 text-center text-text-muted" />
              <button
                type="button"
                onClick={() => ctx.onPreview(f)}
                title={`Preview ${f.filename}`}
                className="min-w-0 flex-1 truncate text-left text-sm text-text-heading hover:text-accent"
              >
                {f.filename}
              </button>
              <span className="shrink-0 text-xs text-text-muted">{fmtBytes(f.size)}</span>
              <button
                onClick={() => ctx.onDeleteFile(f.id, f.filename)}
                title="Delete file"
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-xs text-text-muted opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
